import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ParticipatingEvent,
  ParticipatingEventInterest,
  getParticipatingEvent,
  getParticipatingEventInterests,
  deleteParticipatingEventInterest,
} from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import './AdminPromotions.css';

const LIST_PATH = '/admin/participating-events';

type ConfirmState = null | { kind: 'remove_interest'; interest: ParticipatingEventInterest };

const AdminParticipatingEventInterests: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = id ? parseInt(id, 10) : NaN;

  const [event, setEvent] = useState<ParticipatingEvent | null>(null);
  const [interests, setInterests] = useState<ParticipatingEventInterest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);

  const load = useCallback(async () => {
    if (isNaN(eventId)) {
      setError('Invalid event');
      setEvent(null);
      setInterests([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const [evRes, intRes] = await Promise.all([
        getParticipatingEvent(eventId),
        getParticipatingEventInterests(eventId),
      ]);
      setEvent(evRes.data);
      setInterests(intRes.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load interested users');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const requestRemoveInterest = (interest: ParticipatingEventInterest) => {
    setConfirmState({ kind: 'remove_interest', interest });
  };

  const handleConfirmRemove = async () => {
    if (!confirmState || confirmState.kind !== 'remove_interest' || isNaN(eventId)) return;
    try {
      await deleteParticipatingEventInterest(eventId, confirmState.interest.id);
      setConfirmState(null);
      await load();
    } catch (e) {
      console.error(e);
      alert('Failed to remove interest');
    }
  };

  const confirmCopy =
    confirmState?.kind === 'remove_interest'
      ? {
          title: 'Remove interest',
          message: `Remove interest from ${
            confirmState.interest.name || 'this entry'
          }? This frees one seat and cannot be undone.`,
          confirmLabel: 'Remove',
        }
      : null;

  if (loading) {
    return <div className="loading">Loading interested users…</div>;
  }

  if (error || !event) {
    return (
      <div className="admin-promotions">
        <Link to={LIST_PATH} className="admin-subpage-back">
          ← Back to Participating Events
        </Link>
        <p className="empty-state">{error || 'Event not found'}</p>
      </div>
    );
  }

  return (
    <div className="admin-promotions">
      <Link to={LIST_PATH} className="admin-subpage-back">
        ← Back to Participating Events
      </Link>

      <div className="select-customer-step participating-interests-step">
        <div className="select-customer-box select-customer-box--interests">
          <h3>Interested Users – {event.title}</h3>
          <p style={{ margin: '0 0 12px', color: '#666', fontSize: 14 }}>
            Seats: {event.booked_seats} / {event.total_seats} booked
            {' · '}
            <Link to={`${LIST_PATH}/${event.id}/edit`} style={{ color: 'var(--tt-interactive)' }}>
              Edit event
            </Link>
          </p>

          {interests.length === 0 ? (
            <p>No interested users yet.</p>
          ) : (
            <div className="participating-interests-table-wrap">
              <table className="promotions-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>Notes</th>
                    <th>Created At</th>
                    <th className="cell-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {interests.map((i) => (
                    <tr key={i.id}>
                      <td>{i.name}</td>
                      <td>{i.mobile || '-'}</td>
                      <td>{i.email || '-'}</td>
                      <td className="cell-notes">{i.notes || '—'}</td>
                      <td>{new Date(i.created_at).toLocaleString()}</td>
                      <td className="cell-actions">
                        <button type="button" className="delete-button" onClick={() => requestRemoveInterest(i)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirmState && confirmCopy && (
        <ConfirmDialog
          open
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmLabel={confirmCopy.confirmLabel}
          cancelLabel="Cancel"
          danger
          onCancel={() => setConfirmState(null)}
          onConfirm={handleConfirmRemove}
        />
      )}
    </div>
  );
};

export default AdminParticipatingEventInterests;
